import {
  DEFAULT_PIPELINE_SETTINGS,
  PROPOSAL_SIGNED_WON_REASON,
  pipelineSettingsSchema,
  stageEntryRequirementsSchema,
  type DealAmountSource,
  type DealRequirableField,
  type DealStatus,
  type PipelineSettings,
  type StageEntryRequirements,
  type StageKind,
} from '@buildbee/contracts';
import { applyBps } from '../common';
import { daysBetween, startOfDayInZone, todayInZone } from './dates';

/**
 * Professional deal lifecycle (Pipedrive / HubSpot / Salesforce style):
 *
 *  - `status` (open | won | lost) is separate from the pipeline stage. The board shows open deals
 *    by stage; Won and Lost are explicit actions that record a close date, final amount and reason.
 *  - Every stage or status change is a `deal_stage_history` row with the time spent in the
 *    previous stage; `stage_entered_at` restarts on every stage change.
 *  - Probability comes from the stage unless the deal overrides it; weighted amount = amount x p.
 *  - A deal "rots" when it sits idle (no stage change and no logged activity) longer than the
 *    stage's `rotting_days` (or the pipeline default).
 *  - Stages can require fields or documents before a deal may enter them.
 *
 * Pure functions only; the API module (apps/api/src/modules/crm/lifecycle.ts) does the I/O.
 */

const SECOND_MS = 1000;

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/** `pipeline.settings` with every default applied (invalid stored JSON falls back to defaults). */
export function resolvePipelineSettings(raw: unknown): PipelineSettings {
  const parsed = pipelineSettingsSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : DEFAULT_PIPELINE_SETTINGS;
}

/** `stage.entry_requirements` with defaults applied. */
export function resolveEntryRequirements(raw: unknown): StageEntryRequirements {
  const parsed = stageEntryRequirementsSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : stageEntryRequirementsSchema.parse({});
}

// ---------------------------------------------------------------------------
// Probability, weighted amount, rotting
// ---------------------------------------------------------------------------

export type ProbabilityDeal = { status: DealStatus; probabilityOverrideBps?: number | null };

/** Won deals count 100 %, lost 0 %; open deals use the override, else the stage's probability. */
export function effectiveProbabilityBps(deal: ProbabilityDeal, stage: { probabilityBps: number } | null | undefined): number {
  if (deal.status === 'won') return 10_000;
  if (deal.status === 'lost') return 0;
  const p = deal.probabilityOverrideBps ?? stage?.probabilityBps ?? 0;
  return Math.min(10_000, Math.max(0, p));
}

export function weightedAmountCents(valueCents: number, probabilityBps: number): number {
  return applyBps(valueCents, probabilityBps);
}

/** Days before an idle open deal is flagged: the stage's own setting, else the pipeline default. */
export function rottingThresholdDays(stage: { rottingDays?: number | null }, settings: Pick<PipelineSettings, 'defaultRottingDays'>): number | null {
  const days = stage.rottingDays ?? settings.defaultRottingDays;
  return days && days > 0 ? days : null;
}

export type IdleDeal = { status: DealStatus; stageEnteredAt: Date; lastActivityAt?: Date | null };

/** Last time anything happened on the deal: the stage change or the latest logged activity. */
export function lastTouchedAt(deal: Pick<IdleDeal, 'stageEnteredAt' | 'lastActivityAt'>): Date {
  const a = deal.lastActivityAt;
  return a && a.getTime() > deal.stageEnteredAt.getTime() ? a : deal.stageEnteredAt;
}

/** Whole days since the deal was last touched. */
export function idleDays(deal: Pick<IdleDeal, 'stageEnteredAt' | 'lastActivityAt'>, now: Date): number {
  return Math.max(0, Math.floor(daysBetween(lastTouchedAt(deal), now)));
}

export function dealRotting(
  deal: IdleDeal,
  stage: { rottingDays?: number | null },
  settings: Pick<PipelineSettings, 'defaultRottingDays'>,
  now: Date,
): { rotting: boolean; idleDays: number; thresholdDays: number | null } {
  const thresholdDays = rottingThresholdDays(stage, settings);
  const idle = idleDays(deal, now);
  return { rotting: deal.status === 'open' && thresholdDays !== null && idle > thresholdDays, idleDays: idle, thresholdDays };
}

// ---------------------------------------------------------------------------
// Stage entry requirements
// ---------------------------------------------------------------------------

export const REQUIRABLE_FIELD_LABELS: Record<DealRequirableField, string> = {
  contactId: 'a contact',
  companyId: 'a company',
  ownerId: 'an owner',
  valueCents: 'a deal value',
  expectedCloseDate: 'an expected close date',
  source: 'a lead source',
  address: 'a job address',
  bidDueAt: 'a bid due date',
};

export type EntryFacts = {
  deal: Partial<Record<DealRequirableField, unknown>>;
  /** Non-archived, non-superseded estimates linked to the deal. */
  estimateCount: number;
  /** A linked proposal is sent, viewed or signed. */
  hasSentProposal: boolean;
  hasSignedProposal: boolean;
};

function present(field: DealRequirableField, value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (field === 'valueCents') return typeof value === 'number' && value > 0;
  if (field === 'address') {
    if (typeof value !== 'object') return false;
    const a = value as Record<string, unknown>;
    return ['line1', 'city', 'postalCode'].some((k) => typeof a[k] === 'string' && (a[k] as string).trim() !== '');
  }
  return true;
}

/** Human-readable list of what is missing before a deal may enter a stage (empty = allowed). */
export function missingEntryRequirements(requirements: StageEntryRequirements, facts: EntryFacts): string[] {
  const missing: string[] = [];
  for (const f of requirements.requiredFields) if (!present(f, facts.deal[f])) missing.push(`add ${REQUIRABLE_FIELD_LABELS[f]}`);
  if (requirements.requireEstimate && facts.estimateCount === 0) missing.push('create an estimate');
  if (requirements.requireSentProposal && !facts.hasSentProposal && !facts.hasSignedProposal) missing.push('send a proposal');
  if (requirements.requireSignedProposal && !facts.hasSignedProposal) missing.push('get the proposal signed');
  return missing;
}

export function entryRequirementsMessage(stageName: string, missing: readonly string[]): string {
  const list = missing.length > 1 ? `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}` : (missing[0] ?? '');
  return `Before moving to "${stageName}", ${list}.`;
}

export function hasEntryRequirements(r: StageEntryRequirements): boolean {
  return r.requiredFields.length > 0 || r.requireEstimate || r.requireSentProposal || r.requireSignedProposal;
}

/** Short summary of a stage's requirements for settings screens ("Estimate, expected close date"). */
export function describeEntryRequirements(r: StageEntryRequirements): string[] {
  const out = r.requiredFields.map((f) => REQUIRABLE_FIELD_LABELS[f].replace(/^(a|an) /, ''));
  if (r.requireEstimate) out.push('estimate');
  if (r.requireSentProposal) out.push('sent proposal');
  if (r.requireSignedProposal) out.push('signed proposal');
  return out;
}

// ---------------------------------------------------------------------------
// Stage history
// ---------------------------------------------------------------------------

/** Seconds spent in the previous stage (never negative, null when the entry time is unknown). */
export function stageDurationSeconds(enteredAt: Date | null | undefined, at: Date): number | null {
  if (!enteredAt) return null;
  return Math.max(0, Math.round((at.getTime() - enteredAt.getTime()) / SECOND_MS));
}

export type HistoryRow = {
  fromStageId: string | null;
  toStageId: string | null;
  fromStatus: DealStatus | null;
  toStatus: DealStatus;
  changedAt: Date;
};

/**
 * The open stage a closed deal should return to on reopen: the most recent open stage it was in
 * (that still exists). Falls back to the deal's current stage when open, else the first open stage.
 */
export function lastOpenStageId(
  history: readonly HistoryRow[],
  stages: readonly { id: string; kind: StageKind; position: number }[],
  currentStageId: string,
): string | null {
  const openIds = new Set(stages.filter((s) => s.kind === 'open').map((s) => s.id));
  const sorted = [...history].sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
  for (const h of sorted) {
    if (h.toStageId && openIds.has(h.toStageId)) return h.toStageId;
    if (h.fromStageId && openIds.has(h.fromStageId)) return h.fromStageId;
  }
  if (openIds.has(currentStageId)) return currentStageId;
  const first = stages.filter((s) => s.kind === 'open').sort((a, b) => a.position - b.position)[0];
  return first?.id ?? null;
}

// ---------------------------------------------------------------------------
// Closing: dates, amounts, reasons
// ---------------------------------------------------------------------------

/**
 * Timestamp to store for a close date picked in the workspace time zone: today means "now";
 * another day means local noon of that day (stable for monthly reports in any zone).
 */
export function closeDateToTimestamp(date: string, timeZone: string, now: Date = new Date()): Date {
  if (date === todayInZone(timeZone, now)) return now;
  const start = startOfDayInZone(date, timeZone);
  return new Date(start.getTime() + 12 * 3600 * SECOND_MS);
}

/** A close date may not be in the future (relative to the workspace's today). */
export function isFutureCloseDate(date: string, timeZone: string, now: Date = new Date()): boolean {
  return date > todayInZone(timeZone, now);
}

export type AmountCandidate = { id: string; status: string; totalCents: number; at: Date | null };

export type SuggestedAmount = { valueCents: number; source: DealAmountSource; sourceId: string | null; label: string };

/**
 * Default final amount when marking a deal won: the latest signed proposal, else the latest
 * accepted estimate, else the latest sent/viewed proposal, else the deal's current value.
 */
export function suggestedWonAmount(
  deal: { valueCents: number },
  proposals: readonly (AmountCandidate & { number: string })[],
  estimates: readonly (AmountCandidate & { number: string })[],
): SuggestedAmount {
  const latest = <T extends AmountCandidate>(rows: readonly T[], statuses: readonly string[]) =>
    rows.filter((r) => statuses.includes(r.status)).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0))[0];
  const signed = latest(proposals, ['signed']);
  if (signed) return { valueCents: signed.totalCents, source: 'proposal', sourceId: signed.id, label: `Signed proposal ${signed.number}` };
  const accepted = latest(estimates, ['accepted']);
  if (accepted) return { valueCents: accepted.totalCents, source: 'estimate', sourceId: accepted.id, label: `Accepted estimate ${accepted.number}` };
  const sent = latest(proposals, ['sent', 'viewed']);
  if (sent) return { valueCents: sent.totalCents, source: 'proposal', sourceId: sent.id, label: `Proposal ${sent.number}` };
  return { valueCents: deal.valueCents, source: 'manual', sourceId: null, label: 'Current deal value' };
}

export type ReasonLike = { id: string; label: string; requiresNote: boolean; archivedAt?: Date | null };

/** Validate a won/lost reason choice; returns an error message or null. */
export function reasonError(
  kind: 'won' | 'lost',
  reason: ReasonLike | null | undefined,
  note: string | null | undefined,
  required: boolean,
): string | null {
  if (!reason) return required ? (kind === 'lost' ? 'Choose why the deal was lost' : 'Choose why the deal was won') : null;
  if (reason.archivedAt) return `"${reason.label}" is no longer an active ${kind} reason`;
  if (reason.requiresNote && !note?.trim()) return `Add a note for "${reason.label}"`;
  return null;
}

/** Find a reason by its label (case-insensitive), for API clients that send text. */
export function findReasonByLabel<T extends ReasonLike>(reasons: readonly T[], label: string | null | undefined): T | undefined {
  const q = label?.trim().toLowerCase();
  if (!q) return undefined;
  return reasons.find((r) => !r.archivedAt && r.label.trim().toLowerCase() === q);
}

// ---------------------------------------------------------------------------
// Proposal automation
// ---------------------------------------------------------------------------

export type ProposalSignedDecision =
  | { kind: 'none' }
  | { kind: 'prompt'; valueCents: number; wonReason: string }
  | { kind: 'mark_won'; valueCents: number; wonReason: string }
  | { kind: 'move_to_stage'; stageId: string };

/** What a signed proposal does to its deal under the pipeline's settings. */
export function proposalSignedDecision(
  settings: Pick<PipelineSettings, 'proposalSigned'>,
  deal: { status: DealStatus; stageId: string },
  signedTotalCents: number,
  stages: readonly { id: string; kind: StageKind }[],
): ProposalSignedDecision {
  if (deal.status !== 'open') return { kind: 'none' };
  const { action, stageId } = settings.proposalSigned;
  if (action === 'mark_won') return { kind: 'mark_won', valueCents: signedTotalCents, wonReason: PROPOSAL_SIGNED_WON_REASON };
  if (action === 'prompt') return { kind: 'prompt', valueCents: signedTotalCents, wonReason: PROPOSAL_SIGNED_WON_REASON };
  if (action === 'move_to_stage') {
    const target = stageId ? stages.find((s) => s.id === stageId && s.kind === 'open') : undefined;
    if (!target || target.id === deal.stageId) return { kind: 'none' };
    return { kind: 'move_to_stage', stageId: target.id };
  }
  return { kind: 'none' };
}

/** Stage a sent proposal moves its deal to (only forward, open stages, never backwards). */
export function proposalSentTarget(
  settings: Pick<PipelineSettings, 'proposalSent'>,
  deal: { status: DealStatus; stageId: string },
  stages: readonly { id: string; kind: StageKind; position: number }[],
): string | null {
  if (deal.status !== 'open' || settings.proposalSent.action !== 'move_to_stage' || !settings.proposalSent.stageId) return null;
  const target = stages.find((s) => s.id === settings.proposalSent.stageId && s.kind === 'open');
  const current = stages.find((s) => s.id === deal.stageId);
  if (!target || target.id === deal.stageId) return null;
  if (current && current.kind === 'open' && current.position > target.position) return null;
  return target.id;
}

/** Validate settings against the pipeline's stages and reasons; returns error messages. */
export function validatePipelineSettings(
  settings: PipelineSettings,
  stages: readonly { id: string; kind: StageKind }[],
  lostReasonIds: ReadonlySet<string>,
): string[] {
  const errors: string[] = [];
  const isOpenStage = (id: string | null) => !!id && stages.some((s) => s.id === id && s.kind === 'open');
  if (settings.proposalSigned.action === 'move_to_stage' && !isOpenStage(settings.proposalSigned.stageId)) {
    errors.push('Pick an open stage of this pipeline for signed proposals');
  }
  if (settings.proposalSent.action === 'move_to_stage' && !isOpenStage(settings.proposalSent.stageId)) {
    errors.push('Pick an open stage of this pipeline for sent proposals');
  }
  if (settings.proposalDeclined.action === 'mark_lost' && settings.requireLostReason) {
    const r = settings.proposalDeclined.lostReasonId;
    if (!r || !lostReasonIds.has(r)) errors.push('Pick the lost reason to use when a client declines');
  }
  return errors;
}
